import { useState, useCallback, useRef, useEffect } from 'react';

// Ask Bloom for the collector: the signed-in user's free-form assistant, placed
// on their My Collection home. Visually mirrors the brand dashboard's
// BrandAssistant (same "assistant-*" styling). The three supported intents are
// shown as prompt chips.
//
// NOTE: the backend flows are not built yet (planned, separate sessions):
//   1. Insurance / document lookup  — read, user-scoped NL→SQL
//   2. Schedule a maintenance request — write: lands in the item's merchant
//      queue → pickup/drop-off → repair logged to the repair_record chain →
//      return to the customer
//   3. Portfolio value               — computed from live data
// Until those exist, submit() shows a friendly placeholder so the card is live.
// Wire submit() to the user assistant endpoint when the flows are implemented.
const SUGGESTIONS = [
    'My necklace lost a gem and needs repair',
    'Please help me with the insurance details for my bracelet',
    'What is the current value of my portfolio?',
];

const PLACEHOLDER_ANSWER =
    "Ask Bloom for your collection is coming soon — I'll be able to answer this here shortly.";

const SpeechRecognition =
    typeof window !== 'undefined' && (window.SpeechRecognition || window.webkitSpeechRecognition);
const speechSupported = !!SpeechRecognition;

const DICTATION_LOCALE =
    (typeof navigator !== 'undefined' && (navigator.language || (navigator.languages && navigator.languages[0]))) || 'en-US';

const MicIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="9" y="2" width="6" height="12" rx="3" />
        <path d="M5 10v1a7 7 0 0 0 14 0v-1" />
        <line x1="12" y1="18" x2="12" y2="22" />
        <line x1="8" y1="22" x2="16" y2="22" />
    </svg>
);

const UserAssistant = () => {
    const [question, setQuestion] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [listening, setListening] = useState(false);
    const recognitionRef = useRef(null);
    const inputRef = useRef(null);
    const timerRef = useRef(null);

    // Grow the textarea to fit its content so the full question stays visible.
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }, [question]);

    // Clear any pending placeholder timer on unmount.
    useEffect(() => () => clearTimeout(timerRef.current), []);

    const submit = useCallback((text) => {
        const q = (text ?? question).trim();
        if (!q || loading) return;
        setLoading(true);
        setResult(null);
        // TODO(user-assistant backend): replace with a POST to the user
        // assistant endpoint once the read/action/valuation flows are wired.
        timerRef.current = setTimeout(() => {
            setResult({ answer: PLACEHOLDER_ANSWER });
            setLoading(false);
        }, 400);
    }, [question, loading]);

    const toggleMic = useCallback(() => {
        if (!speechSupported) return;
        if (listening) { recognitionRef.current?.stop(); return; }
        const rec = new SpeechRecognition();
        rec.lang = DICTATION_LOCALE;
        rec.interimResults = false;
        rec.maxAlternatives = 1;
        rec.onresult = (e) => {
            const transcript = e.results[0][0].transcript;
            setQuestion(transcript);
            submit(transcript);
        };
        rec.onend = () => setListening(false);
        rec.onerror = () => setListening(false);
        recognitionRef.current = rec;
        setListening(true);
        rec.start();
    }, [listening, submit]);

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
    };

    const reset = useCallback(() => {
        recognitionRef.current?.stop();
        clearTimeout(timerRef.current);
        setQuestion('');
        setResult(null);
        setLoading(false);
        inputRef.current?.focus();
    }, []);

    return (
        <div className="dpp-card insights-card assistant-card">
            <div className="insights-card-title">Ask Bloom</div>

            <div className="assistant-input-row">
                <button
                    type="button"
                    className={`assistant-mic${listening ? ' is-listening' : ''}`}
                    onClick={toggleMic}
                    disabled={!speechSupported}
                    title={speechSupported ? (listening ? 'Stop' : 'Speak your question') : 'Voice input not supported'}
                    aria-label={listening ? 'Stop listening' : 'Speak your question'}
                >
                    {MicIcon}
                </button>
                <textarea
                    ref={inputRef}
                    className="dpp-input assistant-input"
                    rows={1}
                    placeholder=""
                    value={question}
                    onChange={e => setQuestion(e.target.value)}
                    onKeyDown={onKeyDown}
                />
                <button className="dpp-button" onClick={() => submit()} disabled={loading || !question.trim()}>
                    {loading ? '…' : 'Ask'}
                </button>
            </div>

            {listening && (
                <div className="assistant-listening-status" role="status" aria-live="polite">
                    <span className="assistant-listening-dot" aria-hidden="true" />
                    Listening…
                </div>
            )}

            {!result && !loading && (
                <div className="assistant-suggestions">
                    {SUGGESTIONS.map(s => (
                        <button key={s} className="assistant-chip" onClick={() => { setQuestion(s); submit(s); }}>
                            {s}
                        </button>
                    ))}
                </div>
            )}

            {loading && <p className="assistant-status">Thinking…</p>}

            {result && (
                <div className="assistant-result">
                    <p className="assistant-answer">{result.answer}</p>
                    <div className="assistant-clear-row">
                        <button type="button" className="assistant-clear" onClick={reset}>
                            Clear
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserAssistant;
